/**
 * FORMULÁRIO DE PREMISSAS DE CRESCIMENTO
 * 
 * Permite editar taxas de crescimento de receita para:
 * - Sétima (2025-2029 + perpetuidade)
 * - 555 Studios (2025-2029 + perpetuidade)
 */

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Divider,
  Typography,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  Alert,
  AlertTitle,
  useTheme,
} from '@mui/material';
import { TrendingUp, Business, Videocam } from '@mui/icons-material';
import { Calculator, Info } from 'lucide-react';
import { useState } from 'react';

import { PercentageInput } from './CommonInputs';
import { RateModeToggle } from '../ui/RateModeToggle';
import { realToNominal } from '../../utils/fisherCalculator';
import type { CompanyAssumptions, RateMode } from '../../types/valuation';

// ============================================================================
// TYPES
// ============================================================================

export interface GrowthAssumptionsFormProps {
  setimaValues: Pick<CompanyAssumptions, 'crescimento'>;
  studio555Values: Pick<CompanyAssumptions, 'crescimento'>;
  onSetimaChange: (values: Pick<CompanyAssumptions, 'crescimento'>) => void;
  onStudio555Change: (values: Pick<CompanyAssumptions, 'crescimento'>) => void;
  disabled?: boolean;
  firstProjectionYear?: number; // 🆕 Ano inicial das projeções (ex: 2025, 2026, 2027...)
}

type Company = 'setima' | 'studio555';

// ============================================================================
// VALORES PADRÃO
// ============================================================================

const DEFAULTS = {
  setima: {
    crescimento: {
      2025: 0.15,
      2026: 0.12,
      2027: 0.10,
      2028: 0.08,
      2029: 0.06,
      perpetuo: 0.03,
    },
  },
  studio555: {
    crescimento: {
      2025: 0.50,
      2026: 0.40,
      2027: 0.30,
      2028: 0.20,
      2029: 0.15,
      perpetuo: 0.03,
    },
  },
};

// ============================================================================
// COMPONENT
// ============================================================================

export function GrowthAssumptionsForm({
  setimaValues,
  studio555Values,
  onSetimaChange,
  onStudio555Change,
  disabled = false,
  firstProjectionYear = 2025, // 🆕 Default para backward compatibility
}: GrowthAssumptionsFormProps) {
  
  const theme = useTheme();
  const [selectedCompany, setSelectedCompany] = useState<Company>('setima');
  const [gMode, setGMode] = useState<RateMode>('nominal'); // Default: nominal
  const [inflacao] = useState(0.045); // TODO: pegar de MacroAssumptions

  // 🆕 Calcular anos dinâmicos baseado em firstProjectionYear
  const projectionYears = [
    firstProjectionYear,
    firstProjectionYear + 1,
    firstProjectionYear + 2,
    firstProjectionYear + 3,
    firstProjectionYear + 4,
  ];
  const lastProjectionYear = projectionYears[projectionYears.length - 1];

  const currentValues = selectedCompany === 'setima' ? setimaValues : studio555Values;
  const currentOnChange = selectedCompany === 'setima' ? onSetimaChange : onStudio555Change;
  const currentDefaults = DEFAULTS[selectedCompany];

  // ✅ ANOS DINÂMICOS: handleChange aceita qualquer número ou 'perpetuo'
  const handleChange = (year: number | 'perpetuo') => (value: number) => {
    currentOnChange({
      crescimento: {
        ...currentValues.crescimento,
        [year]: value,
      },
    });
  };

  const handleReset = () => {
    currentOnChange(currentDefaults);
  };

  // Calcular CAGR (Compound Annual Growth Rate)
  const year1 = projectionYears[0];
  const year2 = projectionYears[1];
  const year3 = projectionYears[2];
  const year4 = projectionYears[3];
  const year5 = projectionYears[4];
  
  const cagr = Math.pow(
    (1 + (currentValues.crescimento[year1] || 0)) *
    (1 + (currentValues.crescimento[year2] || 0)) *
    (1 + (currentValues.crescimento[year3] || 0)) *
    (1 + (currentValues.crescimento[year4] || 0)) *
    (1 + (currentValues.crescimento[year5] || 0)),
    1 / 5
  ) - 1;

  return (
    <Card>
      <CardHeader
        title="Premissas de Crescimento de Receita"
        subheader={`Taxas anuais de crescimento (${firstProjectionYear}-${lastProjectionYear} + perpetuidade)`}
        avatar={<TrendingUp />}
        action={
          <Chip
            label="Reset Padrões"
            onClick={handleReset}
            disabled={disabled}
            size="small"
            variant="outlined"
          />
        }
      />
      <Divider />
      <CardContent>
        {/* Seletor de empresa */}
        <Stack direction="row" spacing={2} sx={{ mb: 3 }} justifyContent="center">
          <ToggleButtonGroup
            value={selectedCompany}
            exclusive
            onChange={(_, newValue) => newValue && setSelectedCompany(newValue)}
            disabled={disabled}
          >
            <ToggleButton value="setima">
              <Business sx={{ mr: 1 }} />
              Sétima
            </ToggleButton>
            <ToggleButton value="studio555">
              <Videocam sx={{ mr: 1 }} />
              555 Studios
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {/* RateModeToggle para g (crescimento perpétuo) */}
        <Box sx={{ mb: 3 }}>
          <RateModeToggle
            value={gMode}
            onChange={setGMode}
            label="Base do Crescimento Perpétuo (g)"
            tooltip="Real: ajustado pela inflação (3% real) | Nominal: inclui inflação (7.635% nominal = 3% real + 4.5% inflação)"
            fullWidth={false}
          />
        </Box>

        {/* Alert de conversão Fisher (só aparece se g for real) */}
        {gMode === 'real' && (
          <Alert severity="info" sx={{ mb: 3 }} icon={<Calculator size={20} />}>
            <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Calculator size={18} /> Conversão Fisher Automática
            </AlertTitle>
            <Typography variant="body2">
              O crescimento perpétuo <strong>{(currentValues.crescimento.perpetuo * 100).toFixed(1)}% real</strong> será
              convertido para <strong>{(realToNominal(currentValues.crescimento.perpetuo, inflacao) * 100).toFixed(2)}% nominal</strong> no
              cálculo do Terminal Value.
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
              Fórmula: (1 + {(currentValues.crescimento.perpetuo * 100).toFixed(1)}%) × (1 + {(inflacao * 100).toFixed(1)}%) - 1 = {(realToNominal(currentValues.crescimento.perpetuo, inflacao) * 100).toFixed(2)}%
            </Typography>
          </Alert>
        )}

        {/* Inputs de crescimento */}
        <Grid container spacing={3}>
          {[...projectionYears, 'perpetuo' as const].map((year) => {
            // ✅ ANOS DINÂMICOS: Usar ano diretamente como chave
            const yearKey = year as number | 'perpetuo';
            
            return (
              <Grid item xs={12} sm={6} md={4} key={year}>
                <PercentageInput
                  label={year === 'perpetuo' ? 'Perpetuidade' : `${year}`}
                  value={currentValues.crescimento[yearKey] || 0}
                  onChange={handleChange(yearKey)}
                  min={-50}
                  max={year === 'perpetuo' ? 10 : 200}
                  helpText={
                    year === 'perpetuo'
                      ? 'Taxa de crescimento perpétuo (Gordon Growth)'
                      : `Taxa de crescimento em ${year}`
                  }
                  disabled={disabled}
                  fullWidth
                />
              </Grid>
            );
          })}
        </Grid>

        {/* Estatísticas */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            <strong>Estatísticas:</strong>
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                CAGR {firstProjectionYear}-{lastProjectionYear}:
              </Typography>
              <Typography variant="h6" color="primary">
                {(cagr * 100).toFixed(1)}%
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Média anual:
              </Typography>
              <Typography variant="h6">
                {(Object.values(currentValues.crescimento)
                  .filter((_, i) => i < 5) // Apenas os 5 anos de projeção
                  .reduce((a, b) => a + b, 0) / 5 * 100).toFixed(1)}%
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Perpetuidade:
              </Typography>
              <Typography variant="h6">
                {(currentValues.crescimento.perpetuo * 100).toFixed(1)}%
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Comparação entre empresas */}
        <Box sx={{ 
          mt: 2, 
          p: 2, 
          bgcolor: theme.palette.mode === 'dark' 
            ? 'rgba(144, 202, 249, 0.08)' // Azul translúcido no dark
            : 'rgba(33, 150, 243, 0.08)',  // Azul translúcido no light
          borderRadius: 1,
          border: '1px solid',
          borderColor: theme.palette.mode === 'dark'
            ? 'rgba(144, 202, 249, 0.2)'
            : 'rgba(33, 150, 243, 0.2)',
        }}>
          <Typography 
            variant="caption" 
            display="block" 
            gutterBottom 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
              color: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.87)'
                : 'rgba(0, 0, 0, 0.87)',
            }}
          >
            <Info size={14} />
            <strong>Dica:</strong>
          </Typography>
          <Typography 
            variant="caption" 
            component="div"
            sx={{
              color: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.7)'
                : 'rgba(0, 0, 0, 0.7)',
            }}
          >
            • <strong>Sétima:</strong> Empresa em recuperação (crescimento moderado)
            <br />
            • <strong>555 Studios:</strong> Startup em hipercrescimento (taxas elevadas)
            <br />
            • Perpetu idade: Geralmente próxima à inflação ou PIB (2-4%)
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
